import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

// The film is nested and written by the local print agent, not the server:
// the artwork lives on a disk here, which Railway can never see. Same engine
// the `npm run print-sheet` command uses, so both build the same sheet.
const AGENT = 'http://127.0.0.1:9187';

const card = { marginBottom: '20px' };
const lbl = { fontSize: '12px', color: '#93827a', marginBottom: '4px' };
const small = { width: '90px' };

export default function DtfSheet() {
  const [agent, setAgent] = useState(null);
  const [agentError, setAgentError] = useState(null);
  const [source, setSource] = useState('forecast');   // 'forecast' | 'pending'
  const [days, setDays] = useState(3);
  const [forecast, setForecast] = useState(null);
  const [forecastError, setForecastError] = useState(null);
  const [rows, setRows] = useState([]);
  const [artwork, setArtwork] = useState([]);
  const [extraCode, setExtraCode] = useState('');
  const [opts, setOpts] = useState({ gapIn: 0.2, maxLenM: 5, angleStep: 15, allowRotation: true });
  const [job, setJob] = useState(null);
  const [building, setBuilding] = useState(false);
  const [awaiting, setAwaiting] = useState([]);        // prints at the printer
  const [ordering, setOrdering] = useState(false);
  const [orderedSheets, setOrderedSheets] = useState([]);
  const [recvQty, setRecvQty] = useState({});          // per-design part delivery
  const [sheets, setSheets] = useState([]);            // films already on disk
  const [booking, setBooking] = useState(null);        // filename being booked
  const [syncedAt, setSyncedAt] = useState(null);
  const poll = useRef(null);

  // ---- the agent -----------------------------------------------------------
  const ping = useCallback(async () => {
    try {
      const r = await axios.get(`${AGENT}/ping`, { timeout: 4000 });
      setAgent(r.data); setAgentError(null); return r.data;
    } catch (e) {
      setAgent(null);
      setAgentError('Print agent not running on this PC. Start it (run-agent.vbs), then hit Retry.');
      return null;
    }
  }, []);

  const loadArtwork = useCallback(async () => {
    try { setArtwork((await axios.get(`${AGENT}/dtf/artwork`, { timeout: 10000 })).data.files || []); }
    catch (e) { setArtwork([]); }
  }, []);

  // ---- what to print -------------------------------------------------------
  // Straight off the forecast — the same numbers the print order is placed
  // from, so the film matches what was ordered.
  const loadForecast = useCallback(async (keepEdits) => {
    try {
      const r = await axios.get(`${API}/api/prints/forecast?days=${days}`);
      setForecast(r.data);
      setSyncedAt(new Date());
      setForecastError(null);
      setRows(prev => {
        const edited = {};
        if (keepEdits) prev.forEach(p => { if (p.edited) edited[p.designCode] = p.qty; });
        // The forecast knows nothing about what is on the disk, so carry the
        // preflight answer across. Without this the artwork and print-size
        // columns blank out on the first refresh and never come back —
        // preflight only re-runs when the set of codes changes, and a refresh
        // usually returns the same codes.
        const known = {};
        prev.forEach(p => {
          if (p.found !== undefined) {
            known[p.designCode] = {
              found: p.found, file: p.file, widthIn: p.widthIn,
              heightIn: p.heightIn, tooBig: p.tooBig, duplicateOf: p.duplicateOf
            };
          }
        });
        const wanted = (r.data.rows || [])
          .map(x => ({
            designCode: x.designCode,
            designName: x.designName,
            suggested: source === 'pending' ? Math.max(0, x.committed - x.have) : x.orderNow,
            confident: x.confident,
            calibrated: x.calibrated
          }))
          .filter(x => x.suggested > 0)
          .map(x => ({
            ...x,
            ...(known[x.designCode] || {}),
            qty: edited[x.designCode] !== undefined ? edited[x.designCode] : x.suggested,
            edited: edited[x.designCode] !== undefined,
            source: 'forecast'
          }));
        const manual = prev.filter(p => p.source === 'manual');
        return [...wanted, ...manual];
      });
    } catch (e) {
      setForecastError(e.response?.data?.message || e.message);
    }
  }, [days, source]);

  // Which file each design maps to, and how big it really prints.
  const preflight = useCallback(async (current) => {
    const items = current.map(r => ({ designCode: r.designCode, designName: r.designName, qty: r.qty }));
    if (!items.length) return;
    try {
      const r = await axios.post(`${AGENT}/dtf/preflight`, { items }, { timeout: 60000 });
      const byCode = {};
      (r.data.rows || []).forEach(x => { byCode[x.designCode] = x; });
      setRows(prev => prev.map(p => ({
        ...p, ...(byCode[p.designCode] || {}),
        qty: p.qty, edited: p.edited, source: p.source, suggested: p.suggested
      })));
    } catch (e) { /* the banner already says the agent is off */ }
  }, []);

  useEffect(() => {
    (async () => { const a = await ping(); if (a) loadArtwork(); await loadForecast(false); })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { loadForecast(true); }, [days, source, loadForecast]);

  // Keep it live: orders land every few minutes, and a film built off a stale
  // number is a film with the wrong prints on it.
  useEffect(() => {
    const t = setInterval(() => { if (!building) loadForecast(true); }, 60000);
    return () => clearInterval(t);
  }, [loadForecast, building]);

  const codes = rows.map(r => r.designCode).join(',');
  useEffect(() => {
    if (!agent || !codes) return;
    const t = setTimeout(() => preflight(rows), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [codes, agent]);

  // ---- editing -------------------------------------------------------------
  const setQty = (code, v) => setRows(prev => prev.map(r =>
    r.designCode === code ? { ...r, qty: Math.max(0, parseInt(v, 10) || 0), edited: true } : r));
  const removeRow = (code) => setRows(prev => prev.filter(r => r.designCode !== code));
  const addManual = () => {
    const code = extraCode.trim().toUpperCase();
    if (!code) return;
    if (rows.some(r => r.designCode === code)) { setExtraCode(''); return; }
    const art = artwork.find(a => a.code === code);
    setRows(prev => [...prev, { designCode: code, designName: art ? art.label : code, qty: 1, edited: true, source: 'manual' }]);
    setExtraCode('');
  };

  // ---- build ---------------------------------------------------------------
  const stopPoll = () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };
  useEffect(() => stopPoll, []);

  // Chrome slows a background tab's timers to about once a minute, so a build
  // that finishes while you are on another tab would still say "Building…"
  // when you come back. Check once, immediately, on return.
  const jobId = job && job.id;
  useEffect(() => {
    if (!jobId || !building) return;
    const onVisible = async () => {
      if (document.hidden) return;
      try {
        const s = await axios.get(`${AGENT}/dtf/job?id=${jobId}`);
        setJob(s.data);
        if (s.data.status !== 'running') { stopPoll(); setBuilding(false); }
      } catch (e) { /* the interval will report it */ }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [jobId, building]);

  const build = async () => {
    const items = rows.filter(r => r.qty > 0 && r.found !== false)
      .map(r => ({ designCode: r.designCode, designName: r.designName, qty: r.qty }));
    if (!items.length) { alert('Nothing to build — every quantity is zero, or no artwork was found.'); return; }
    setBuilding(true);
    setJob({ status: 'running', progress: { message: 'Starting' } });
    try {
      // Only long enough to hand back a job id — the nesting itself is polled,
      // never waited on. It was 15s, which a busy agent could miss, and the
      // build was then reported as failed while the film was being written.
      const r = await axios.post(`${AGENT}/dtf/build`, { items, options: opts }, { timeout: 60000 });
      const id = r.data.jobId;
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${AGENT}/dtf/job?id=${id}`);
          setJob(s.data);
          if (s.data.status !== 'running') { stopPoll(); setBuilding(false); }
        } catch (e) { stopPoll(); setBuilding(false); setJob({ status: 'error', error: e.message }); }
      }, 1000);
    } catch (e) {
      setBuilding(false);
      setJob({ status: 'error', error: e.response?.data?.error || e.message });
    }
  };

  // ---- ordered, then arrived -----------------------------------------------
  // Building the film is not the same as having the transfers. A sheet that
  // has been sent to the printer is recorded as on-order so the forecast stops
  // asking for it again, and stays that way until the prints physically land.
  const sheetManifest = (job && job.status === 'done' && job.result)
    ? job.result.sheets.reduce((acc, s) => {
        Object.entries(s.manifest || {}).forEach(([c, n]) => { acc[c] = (acc[c] || 0) + n; });
        return acc;
      }, {})
    : null;

  const loadAwaiting = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/prints`);
      setAwaiting((r.data || []).filter(p => (p.onOrder || 0) > 0)
        .map(p => ({ designCode: p.designCode, designName: p.designName, onOrder: p.onOrder, stock: p.quantity })));
    } catch (e) { /* the panel simply stays as it was */ }
  }, []);

  // Films already written to disk, with what is on each one. Read from the
  // agent rather than held in page state, so leaving the tab or restarting
  // the agent no longer loses the ability to book a sheet into stock.
  const loadSheets = useCallback(async () => {
    try { setSheets((await axios.get(`${AGENT}/dtf/sheets`, { timeout: 8000 })).data.sheets || []); }
    catch (e) { setSheets([]); }
  }, []);

  useEffect(() => { loadAwaiting(); loadSheets(); }, [loadAwaiting, loadSheets]);

  // Book a sheet that already exists. Two ways in, because both really
  // happen: the film has gone to the printer and is not back yet
  // ('onOrder'), or the transfers are already on the shelf and the on-order
  // step never got used ('stock').
  //
  // Stock goes through /api/prints/update, one code at a time. That is the
  // right call and not an oversight: a manifest lists every code physically
  // on the film, companions included, so a cascading movement would count
  // the second transfer of a two-print garment twice.
  const bookSheet = async (sheet, mode) => {
    const entries = Object.entries(sheet.manifest || {});
    if (!entries.length) return;
    const units = entries.reduce((s, [, n]) => s + n, 0);
    if (!window.confirm(
      (mode === 'stock'
        ? `Add ${units} print(s) from ${sheet.filename} straight onto the shelf?`
        : `Record ${units} print(s) from ${sheet.filename} as sent to the printer?`) +
      `\n\n${entries.map(([c, n]) => `${c} × ${n}`).join('\n')}\n\n` +
      (mode === 'stock'
        ? 'Use this when the transfers are physically here.'
        : 'They will show as on order until you mark them arrived.')
    )) return;
    setBooking(sheet.filename);
    try {
      for (const [designCode, qty] of entries) {
        if (mode === 'stock') await axios.post(`${API}/api/prints/update`, { designCode, quantity: qty });
        else await axios.post(`${API}/api/prints/add-on-order`, { designCode, qty });
      }
      // Only stamped once the stock movement is in, so a failure leaves the
      // sheet still bookable instead of silently marked done.
      try { await axios.post(`${AGENT}/dtf/sheet-recorded`, { file: sheet.path, as: mode, units }); }
      catch (e) { /* the stock is what matters; the badge can lag */ }
      await Promise.all([loadAwaiting(), loadForecast(true), loadSheets()]);
      alert(mode === 'stock'
        ? `✓ ${units} print(s) added to the shelf.`
        : `✓ ${units} print(s) recorded as on order. Mark them arrived when they get here.`);
    } catch (e) {
      alert('Could not book that sheet: ' + (e.response?.data?.message || e.message));
    } finally { setBooking(null); }
  };

  const markOrdered = async () => {
    const entries = Object.entries(sheetManifest || {});
    if (!entries.length) return;
    if (!window.confirm(
      `Record these as sent to the printer?\n\n${entries.map(([c, n]) => `${c} × ${n}`).join('\n')}\n\n` +
      `They'll show as on order, so the forecast stops asking for them again. ` +
      `Mark them arrived once the transfers are physically here.`
    )) return;
    setOrdering(true);
    try {
      for (const [designCode, qty] of entries) {
        await axios.post(`${API}/api/prints/add-on-order`, { designCode, qty });
      }
      setOrderedSheets(prev => [...prev, ...(job.result.sheets.map(s => s.filename))]);
      // Stamp the sidecars too, so the Sheets already built list below agrees
      // with what just happened instead of offering the same film again.
      for (const s of job.result.sheets) {
        try { await axios.post(`${AGENT}/dtf/sheet-recorded`, { file: s.path, as: 'onOrder' }); } catch (e) { /* badge only */ }
      }
      await loadAwaiting();
      await loadForecast(true);
      await loadSheets();
      alert('Recorded as on order. Mark them arrived when the prints get here.');
    } catch (e) {
      alert('Could not record it: ' + (e.response?.data?.message || e.message));
    } finally { setOrdering(false); }
  };

  const receive = async (items, label) => {
    if (!items.length) return;
    if (!window.confirm(
      `Add these to print stock?\n\n${items.map(i => `${i.designCode} × ${i.qty}`).join('\n')}\n\n` +
      `Use this once the transfers are physically on the shelf.`
    )) return;
    try {
      const r = await axios.post(`${API}/api/prints/receive-on-order`, { items });
      await loadAwaiting();
      await loadForecast(true);
      alert(`✓ ${r.data.totalReceived} print(s) added to stock${label ? ' — ' + label : ''}.`);
    } catch (e) {
      alert('Could not receive them: ' + (e.response?.data?.message || e.message));
    }
  };

  const reveal = async (file) => {
    try { await axios.post(`${AGENT}/dtf/reveal`, { file }); }
    catch (e) { alert('Could not open the folder: ' + e.message); }
  };

  // ---- totals --------------------------------------------------------------
  const totalPieces = rows.reduce((s, r) => s + (r.found === false ? 0 : r.qty), 0);
  const rawArea = rows.reduce((s, r) => s + ((r.widthIn || 0) * (r.heightIn || 0) * r.qty), 0);
  const noArt = rows.filter(r => r.found === false);

  // A design with no file on the disk is silently left off the film, so it has
  // to be impossible to miss: it goes to the top of the table and is marked
  // red. Sorted for display only — the underlying order is left alone, and
  // everything is keyed by code, so editing still hits the right row.
  const shownRows = [...rows].sort((a, b) =>
    (a.found === false ? 0 : 1) - (b.found === false ? 0 : 1));

  return (
    <div>
      {agentError && (
        <div className="card" style={{ ...card, borderLeft: '3px solid #b3261e' }}>
          <b style={{ color: '#b3261e' }}>Agent offline.</b>{' '}
          <span style={{ color: '#4a3840' }}>{agentError}</span>
          <button onClick={ping} style={{ marginLeft: '12px', padding: '4px 12px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
        </div>
      )}
      {agent && !agent.dtf.diskConnected && (
        <div className="card" style={{ ...card, borderLeft: '3px solid #7a6a12' }}>
          <b style={{ color: '#7a6a12' }}>Artwork folder not found.</b>{' '}
          <span style={{ color: '#4a3840' }}>Looking in <code>{agent.dtf.artworkDir}</code>. Plug the drive in, then Retry.</span>
          <button onClick={ping} style={{ marginLeft: '12px', padding: '4px 12px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
        </div>
      )}
      {agent && agent.dtf.usingRepoFolder && agent.dtf.diskConnected && (
        <div className="card" style={{ ...card, borderLeft: '3px solid #93827a' }}>
          <span style={{ color: '#7d6a63' }}>Reading artwork from the repo's <code>designs/</code> folder — the external disk isn't connected.</span>
        </div>
      )}

      {noArt.length > 0 && (
        <div className="card" style={{ ...card, background: '#fbe9e7', borderLeft: '3px solid #b3261e' }}>
          <div style={{ color: '#8c1d18', fontWeight: 600, marginBottom: '6px' }}>
            {noArt.length} design{noArt.length > 1 ? 's have' : ' has'} no artwork on the disk and will be left off the film
          </div>
          <div style={{ color: '#b3261e', fontSize: '13px', marginBottom: '8px' }}>
            {noArt.map(r => `${r.designCode}.png`).join('  ·  ')}
          </div>
          <div style={{ color: '#7d6a63', fontSize: '12px' }}>
            Export each one at 300 DPI, at the exact size it should print, on a transparent
            background, into <code>{agent && agent.dtf.artworkDir}</code>. Name it{' '}
            <code>{noArt[0].designCode}.png</code> or <code>{noArt[0].designCode} - {noArt[0].designName}.png</code>, then hit Refresh.
          </div>
        </div>
      )}

      <div className="card" style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div className="card-title" style={{ margin: 0 }}>DTF Gang Sheet — 23in film @ 300 DPI</div>
          <div style={{ fontSize: '12px', color: '#93827a' }}>
            {syncedAt ? `forecast refreshed ${syncedAt.toLocaleTimeString()}` : ''}
            <button onClick={() => loadForecast(true)} style={{ marginLeft: '10px', padding: '4px 12px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>Refresh</button>
          </div>
        </div>

        <p style={{ fontSize: '12px', color: '#93827a', margin: '12px 0 14px' }}>
          Quantities come straight off the print forecast and refresh every minute. Edit any of them, or add a
          design by code. Artwork is read from the disk as <code>CODE.png</code> or <code>CODE - Name.png</code>.
        </p>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div><div style={lbl}>Quantities from</div>
            <select value={source} onChange={e => setSource(e.target.value)} style={{ width: '210px' }}>
              <option value="forecast">Forecast — cover the next few days</option>
              <option value="pending">Pending orders only</option>
            </select></div>
          {source === 'forecast' && (
            <div><div style={lbl}>Days to cover</div>
              <input type="number" min="1" max="30" value={days} onChange={e => setDays(Math.max(1, parseInt(e.target.value, 10) || 3))} style={small} /></div>
          )}
          <div><div style={lbl}>Add a design by code</div>
            <input list="dtf-codes" value={extraCode} placeholder="e.g. UNS001"
              onChange={e => setExtraCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addManual(); }}
              style={{ width: '160px' }} />
            <datalist id="dtf-codes">{artwork.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}</datalist>
            <button onClick={addManual} style={{ marginLeft: '8px', padding: '7px 14px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>Add</button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr><th>Code</th><th>Design</th><th style={{ color: '#7a6a12' }}>Qty</th><th>Artwork file</th><th>Print size</th><th></th></tr>
            </thead>
            <tbody>
              {shownRows.map(r => (
                <tr key={r.designCode} style={r.found === false
                  ? { background: '#fbe9e7', boxShadow: 'inset 3px 0 0 #b3261e' }
                  : undefined}>
                  <td style={{ fontFamily: 'monospace', color: r.found === false ? '#b3261e' : '#8c3242' }}>{r.designCode}</td>
                  <td style={{ fontWeight: 500, color: r.found === false ? '#8c1d18' : undefined }}>
                    {r.designName}
                    {r.found === false && <span style={{ color: '#b3261e', fontSize: '11px', marginLeft: '6px', fontWeight: 700 }}>ARTWORK MISSING</span>}
                    {r.source === 'manual' && <span style={{ color: '#93827a', fontSize: '11px', marginLeft: '6px' }}>added</span>}
                    {r.confident === false && <span style={{ color: '#7a6a12', fontSize: '11px', marginLeft: '6px' }}>thin history</span>}
                  </td>
                  <td>
                    <input type="number" min="0" value={r.qty} onChange={e => setQty(r.designCode, e.target.value)} style={small} />
                    {r.edited && r.suggested !== undefined && r.qty !== r.suggested &&
                      <div style={{ fontSize: '11px', color: '#93827a' }}>forecast said {r.suggested}</div>}
                  </td>
                  <td style={{ color: r.found === false ? '#b3261e' : '#4a3840', fontSize: '13px' }}>
                    {r.found === false ? 'no file with this code' : (r.file || '…')}
                    {r.duplicateOf && <div style={{ color: '#7a6a12', fontSize: '11px' }}>duplicate code, also in {r.duplicateOf}</div>}
                  </td>
                  <td style={{ fontSize: '13px', color: '#4a3840' }}>{r.widthIn ? `${r.widthIn} × ${r.heightIn} in` : '—'}</td>
                  <td>
                    <button onClick={() => removeRow(r.designCode)} title="Leave this design off the film"
                      style={{ fontSize: '13px', lineHeight: 1, padding: '2px 9px', background: '#ffffff', color: '#b3261e', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>×</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: '#2e7d50' }}>
                  {forecastError
                    ? <span style={{ color: '#b3261e' }}>Could not load the forecast: {forecastError}</span>
                    : forecast ? 'Nothing to print — every design is covered.' : 'Loading…'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '14px', color: '#4a3840', fontSize: '14px' }}>
          <b>{totalPieces}</b> piece(s) across <b>{rows.length}</b> design(s)
          {rawArea > 0 && <> · <b>{(rawArea / 23 / 39.3701).toFixed(2)} m</b> of film if they were laid out in a row</>}
          {noArt.length > 0 && <span style={{ color: '#b3261e' }}> · {noArt.length} with no artwork file, and they will be skipped</span>}
        </div>
      </div>

      <div className="card" style={card}>
        <div className="card-title">Film settings</div>
        <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><div style={lbl}>Gap between designs (in)</div>
            <input type="number" step="0.05" value={opts.gapIn} onChange={e => setOpts({ ...opts, gapIn: parseFloat(e.target.value) || 0 })} style={small} /></div>
          <div><div style={lbl}>Split beyond (m)</div>
            <input type="number" step="0.5" value={opts.maxLenM} onChange={e => setOpts({ ...opts, maxLenM: parseFloat(e.target.value) || 5 })} style={small} /></div>
          <div><div style={lbl}>Rotation</div>
            <select
              value={opts.allowRotation ? String(opts.angleStep) : 'none'}
              onChange={e => setOpts(e.target.value === 'none'
                ? { ...opts, allowRotation: false }
                : { ...opts, allowRotation: true, angleStep: parseInt(e.target.value, 10) })}
              style={{ width: '190px' }}>
              <option value="none">None — as exported</option>
              <option value="90">Quarter turns only</option>
              <option value="30">Any angle, 30° steps</option>
              <option value="15">Any angle, 15° steps</option>
              <option value="10">Any angle, 10° steps</option>
            </select></div>
          <button className="btn-primary" onClick={build} disabled={building || !agent || !agent.dtf.diskConnected}
            style={{ fontSize: '14px', padding: '10px 20px', opacity: (building || !agent) ? 0.5 : 1 }}>
            {building ? 'Building…' : 'Build gang sheet'}
          </button>
        </div>
        <p style={{ fontSize: '12px', color: '#93827a', marginTop: '12px', marginBottom: 0 }}>
          Designs nest against each other's actual shape, not their boxes, so the film comes out as short as it
          can. Turning is done pixel for pixel — nothing is ever resampled, so edges stay exactly as hard as they
          were exported. Finer steps pack tighter and take longer.
        </p>
      </div>

      {job && (
        <div className="card" style={card}>
          <div className="card-title">
            {job.status === 'running' ? 'Building…' : job.status === 'error' ? 'Build failed' : 'Film ready'}
          </div>
          {job.status === 'running' && (
            <div style={{ color: '#4a3840' }}>
              {job.progress?.message || 'Working'}
              {job.progress?.total ? ` (${job.progress.done + 1}/${job.progress.total})` : ''}
            </div>
          )}
          {job.status === 'error' && <div style={{ color: '#b3261e' }}>{job.error}</div>}
          {job.status === 'done' && job.result && (
            <>
              {job.result.sheets.map(s => (
                <div key={s.filename} style={{ padding: '10px 0', borderBottom: '1px solid #e6dccc' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontFamily: 'monospace', color: '#2e7d50', fontSize: '15px' }}>{s.filename}</div>
                      <div style={{ fontSize: '12px', color: '#93827a' }}>
                        {s.lengthIn.toFixed(1)}in × {s.heightIn}in · {s.lengthM.toFixed(2)}m used, order <b style={{ color: '#7a6a12' }}>{s.orderMetres}m</b> · {s.pieces} pieces · {(s.coverage * 100).toFixed(0)}% ink
                      </div>
                    </div>
                    <button onClick={() => reveal(s.path)}
                      style={{ padding: '7px 14px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>
                      Show in folder
                    </button>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#7d6a63' }}>
                    {Object.entries(s.manifest).map(([c, n]) => `${c} ×${n}`).join(' · ')}
                  </div>
                  {/* look at the nesting before any film is burned */}
                  <div style={{ marginTop: '12px', background: '#fff', borderRadius: '8px', padding: '8px', overflowX: 'auto' }}>
                    <img src={`${AGENT}/dtf/preview?file=${encodeURIComponent(s.path)}&w=1400`}
                      alt={`Preview of ${s.filename}`}
                      style={{ display: 'block', height: 'auto', maxWidth: 'none', width: `${Math.max(100, (s.lengthIn / s.heightIn) * 22)}%`, minWidth: '100%' }} />
                  </div>
                  <div style={{ fontSize: '11px', color: '#93827a', marginTop: '6px' }}>
                    Preview only — the saved file is the full-resolution 300 DPI PNG.
                  </div>
                </div>
              ))}
              {job.result.missing.length > 0 && (
                <div style={{ marginTop: '12px', color: '#b3261e', fontSize: '13px' }}>
                  Left off: {job.result.missing.map(m => `${m.designCode} (${m.reason})`).join(', ')}
                </div>
              )}
              {job.result.sheets.length === 0 && <div style={{ color: '#b3261e' }}>Nothing was placed.</div>}

              {/* The film existing is not the same as the transfers existing.
                  Recording the sheet as ordered stops the forecast asking for
                  the same prints again on the next refresh. */}
              {job.result.sheets.length > 0 && sheetManifest && (
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e6dccc' }}>
                  {orderedSheets.includes(job.result.sheets[0].filename) ? (
                    <div style={{ color: '#2e7d50', fontSize: '13px' }}>
                      ✓ Recorded as sent to the printer — mark them arrived below once the transfers are here.
                    </div>
                  ) : (<>
                    <button className="btn-primary" onClick={markOrdered} disabled={ordering}
                      style={{ padding: '9px 16px', fontSize: '13px' }}>
                      {ordering ? 'Recording…' : '🖨 Sent to the printer — record as on order'}
                    </button>
                    <div style={{ fontSize: '12px', color: '#93827a', marginTop: '8px' }}>
                      Records {Object.values(sheetManifest).reduce((s, n) => s + n, 0)} print(s) as on order so the
                      forecast stops asking for them again. Stock only goes up when you mark them arrived.
                    </div>
                  </>)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Films already on disk. A sheet used to be bookable only in the sitting
          it was built in — the build job expires after an hour and dies with
          the agent — so an evening's sheet could never be put into stock. */}
      {sheets.length > 0 && (
        <div className="card" style={card}>
          <div className="card-title">Sheets already built — {sheets.length}</div>
          <p style={{ fontSize: '12px', color: '#93827a', margin: '8px 0 14px' }}>
            Every film this PC has written, newest first. Book one in whenever you get to it:
            <strong> on order</strong> if it is still at the printer, <strong>straight to the shelf</strong>
            {' '}if the transfers are already here.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>Sheet</th><th>Built</th><th>Length</th><th>Prints</th><th>What is on it</th><th></th></tr>
              </thead>
              <tbody>
                {sheets.map(s => (
                  <tr key={s.filename}>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{s.filename}</td>
                    <td style={{ fontSize: '12px', color: '#7d6a63' }}>
                      {new Date(s.builtAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ fontSize: '12px' }}>{s.lengthM ? s.lengthM.toFixed(2) + ' m' : '—'}</td>
                    <td style={{ fontSize: '12px' }}>{s.pieces ?? '—'}</td>
                    <td style={{ fontSize: '11px', color: '#7d6a63', maxWidth: '320px' }}>
                      {s.hasManifest
                        ? Object.entries(s.manifest).map(([c, n]) => `${c} ×${n}`).join(' · ')
                        : <span style={{ color: '#b3261e' }}>built before manifests were saved — contents unknown</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {s.recorded ? (
                        <span style={{ color: '#2e7d50', fontSize: '12px' }}>
                          ✓ {s.recorded.as === 'stock' ? 'on the shelf' : 'on order'}
                        </span>
                      ) : s.hasManifest ? (<>
                        <button disabled={booking === s.filename} onClick={() => bookSheet(s, 'onOrder')}
                          style={{ fontSize: '12px', padding: '5px 10px', marginRight: '6px', background: '#fff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>
                          🖨 On order
                        </button>
                        <button disabled={booking === s.filename} onClick={() => bookSheet(s, 'stock')}
                          style={{ fontSize: '12px', padding: '5px 10px', background: '#fff', color: '#2e7d50', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>
                          ⬆ To shelf
                        </button>
                      </>) : <span style={{ fontSize: '12px', color: '#93827a' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Everything sitting at the printer. This is the missing half of the
          loop: the film gets built, then the transfers come back, and until
          they are marked arrived they are not stock. */}
      {awaiting.length > 0 && (
        <div className="card" style={card}>
          <div className="card-title">
            At the printer — {awaiting.reduce((s, a) => s + a.onOrder, 0)} print(s) not yet on the shelf
          </div>
          <p style={{ fontSize: '12px', color: '#93827a', margin: '8px 0 14px' }}>
            These are ordered but not counted as stock yet. Mark them arrived when the transfers are
            physically here — that is what adds them to print stock. Receive a part delivery by
            changing the number first.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>Code</th><th>Design</th><th>On order</th><th>On shelf now</th><th>Arrived</th><th></th></tr>
              </thead>
              <tbody>
                {awaiting.map(a => (
                  <tr key={a.designCode}>
                    <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>{a.designCode}</td>
                    <td style={{ fontWeight: 500 }}>{a.designName}</td>
                    <td style={{ color: '#2e7d50', fontWeight: 600 }}>{a.onOrder}</td>
                    <td style={{ color: a.stock < 0 ? '#b3261e' : '#4a3840' }}>{a.stock}</td>
                    <td>
                      <input type="number" min="1" max={a.onOrder}
                        value={recvQty[a.designCode] ?? a.onOrder}
                        onChange={e => setRecvQty({ ...recvQty, [a.designCode]: Math.max(1, Math.min(a.onOrder, parseInt(e.target.value, 10) || 1)) })}
                        style={small} />
                    </td>
                    <td>
                      <button onClick={() => receive(
                        [{ designCode: a.designCode, qty: recvQty[a.designCode] ?? a.onOrder }], a.designName)}
                        style={{ fontSize: '13px', padding: '6px 12px', background: '#ffffff', color: '#2e7d50', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer' }}>
                        ✓ Arrived
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn-primary" style={{ marginTop: '14px', padding: '9px 16px', fontSize: '13px' }}
            onClick={() => receive(awaiting.map(a => ({ designCode: a.designCode, qty: a.onOrder })), 'whole order')}>
            ✓ The whole order arrived — add all {awaiting.reduce((s, a) => s + a.onOrder, 0)} to stock
          </button>
        </div>
      )}
    </div>
  );
}
